import type { ColumnOrderState, VisibilityState } from '@tanstack/react-table'
import { Search, X } from 'lucide-react'
import { DIFFICULTY_OPTIONS } from '../constants'
import { ColumnVisibilityDropdown } from './column-visibility-dropdown'
import { FilterDropdown } from './filter-dropdown'
import { MultiSelectFilter } from './multi-select-filter'

interface TableControlsProps {
  // Search
  globalFilter: string
  onGlobalFilterChange: (value: string) => void
  // Filters
  difficultyFilter: string | null
  onDifficultyFilterChange: (value: string | null) => void
  sourceFilter: string | null
  onSourceFilterChange: (value: string | null) => void
  sources: string[]
  studyPlanFilter: string[]
  onStudyPlanFilterChange: (values: string[]) => void
  studyPlanOptions: string[]
  tagsFilter: string[]
  onTagsFilterChange: (values: string[]) => void
  tagsOptions: string[]
  // Column visibility
  columnVisibility: VisibilityState
  columnOrder: ColumnOrderState
  onToggleVisibility: (columnId: string) => void
  onShowAllColumns: () => void
  onReorderColumns: (newOrder: ColumnOrderState) => void
  isClientContext: boolean
}

export function TableControls({
  globalFilter,
  onGlobalFilterChange,
  difficultyFilter,
  onDifficultyFilterChange,
  sourceFilter,
  onSourceFilterChange,
  sources,
  studyPlanFilter,
  onStudyPlanFilterChange,
  studyPlanOptions,
  tagsFilter,
  onTagsFilterChange,
  tagsOptions,
  columnVisibility,
  columnOrder,
  onToggleVisibility,
  onShowAllColumns,
  onReorderColumns,
  isClientContext,
}: TableControlsProps) {
  // Check if any filters are active
  const activeFilterCount =
    (difficultyFilter ? 1 : 0) +
    (sourceFilter ? 1 : 0) +
    (studyPlanFilter.length > 0 ? 1 : 0) +
    (tagsFilter.length > 0 ? 1 : 0)

  const handleClearAllFilters = () => {
    onDifficultyFilterChange(null)
    onSourceFilterChange(null)
    onStudyPlanFilterChange([])
    onTagsFilterChange([])
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      {/* Search Input */}
      <div className="relative max-w-md flex-1">
        <Search
          size={16}
          className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          type="text"
          value={globalFilter ?? ''}
          onChange={(e) => onGlobalFilterChange(e.target.value)}
          placeholder="Search by problem name or ID..."
          className="border-border bg-muted w-full rounded-lg border py-2 pr-4 pl-9 text-sm"
        />
        {globalFilter && (
          <button
            type="button"
            onClick={() => onGlobalFilterChange('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Filter Dropdowns */}
      <FilterDropdown
        label="Difficulty"
        value={difficultyFilter}
        options={DIFFICULTY_OPTIONS}
        onChange={onDifficultyFilterChange}
      />

      {sources.length > 0 && (
        <FilterDropdown
          label="Source"
          value={sourceFilter}
          options={sources}
          onChange={onSourceFilterChange}
        />
      )}

      <MultiSelectFilter
        label="Study Plans"
        selectedValues={studyPlanFilter}
        options={studyPlanOptions}
        onChange={onStudyPlanFilterChange}
        variant="violet"
      />

      <MultiSelectFilter
        label="Tags"
        selectedValues={tagsFilter}
        options={tagsOptions}
        onChange={onTagsFilterChange}
        variant="emerald"
      />

      {/* Clear all filters button */}
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={handleClearAllFilters}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
        >
          <X size={14} />
          Clear filters
        </button>
      )}

      {/* Column Visibility Dropdown */}
      <ColumnVisibilityDropdown
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        onToggle={onToggleVisibility}
        onShowAll={onShowAllColumns}
        onReorder={onReorderColumns}
        isClientContext={isClientContext}
      />
    </div>
  )
}
